import { ExceptionEvent, StatusData } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TellTalesComponent from "@/components/grid/rowDetail/tellTaleDetail/TellTalesComponent";
import {
  ActiveTellTale,
  CreateTellTaleOrderDialog,
} from "@/components/maintenance/CreateTellTaleOrderDialog";
import { TELLTALES_DIAGNOSTICS } from "@/constants/constants";
import { tellTales } from "@/constants/TellTales";
import { useDeviceById } from "@/hooks/devices/useDeviceById";
import { useStatusInfoById } from "@/hooks/statusInfos/useStatusInfoById";
import { WarningType } from "@/models/Warning";

const TellTaleModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  modalData: {
    warningType: WarningType;
    event: ExceptionEvent;
  };
}> = ({ open, handleClose, modalData }) => {
  const { t } = useTranslation();
  const { data: tellTaleData } = useStatusInfoById(
    modalData?.event.device.id,
    TELLTALES_DIAGNOSTICS,
  );
  const { data: device } = useDeviceById(modalData?.event?.device?.id);
  const [orderOpen, setOrderOpen] = useState(false);

  // Telltales currently lit red (1) or yellow (2) — the candidates for a check order.
  const activeTellTales = useMemo<ActiveTellTale[]>(() => {
    const statusData = (tellTaleData?.statusData ?? []) as StatusData[];
    return statusData
      .map(sd => {
        const tt = tellTales.find(item => item.id === sd.diagnostic.id);
        const status = (sd.data as number | undefined) ?? 7;
        return tt ? { id: tt.id, name: tt.name, status } : null;
      })
      .filter((tt): tt is ActiveTellTale => tt != null && (tt.status === 1 || tt.status === 2));
  }, [tellTaleData]);

  if (!modalData) return <></>;

  const filteredTellTales = tellTales.filter(item => !item?.name?.includes("Reserved for"));
  const hasAny =
    tellTaleData?.statusData?.some(sd =>
      filteredTellTales.some(tt => tt.id === sd.diagnostic.id),
    ) ?? false;

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "60%",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 8,
            p: 4,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {device?.name && (
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {device?.name}
            </Typography>
          )}

          <Divider sx={{ mb: 2 }} />
          {!hasAny ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <TellTalesComponent
              displayTellTales={filteredTellTales}
              tellTaleData={(tellTaleData?.statusData ?? []) as StatusData[]}
            />
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              disabled={!device}
              onClick={() => setOrderOpen(true)}
            >
              {t("tellTaleOrder.button")}
            </Button>
          </Box>
        </Box>
      </Modal>

      {device && (
        <CreateTellTaleOrderDialog
          open={orderOpen}
          device={device}
          warningType={modalData.warningType}
          ruleId={modalData.event?.rule?.id ?? ""}
          activeTellTales={activeTellTales}
          onClose={() => setOrderOpen(false)}
        />
      )}
    </>
  );
};

export default TellTaleModal;
